import _ from 'lodash';
import semver from 'semver';
import { Vue2 as Component } from 'vue-demi';
import { defineStore } from 'pinia';

import { IAgent } from '@/types';
import {
  AppLauncherSettings,
  MigrationsBySemVerRange,
  VersionInfo,
  UpdateMeta,
  UpdateReports,
  AppError,
} from '../types';
import {
  needsDataUpdate,
  orderVersionsBySemVer,
  getMigrationsByAppVersion,
  getMaxAppVersionByMigration,
} from '../common';
import {
  APP_STATES,
  APP_MIN_VERSION,
  DEFAULT_APP_SETTINGS,
  DEFAULT_AVATAR_SETTINGS,
  MIGRATIONS,
} from '../constants';
import { USER_ROLE } from '@/constants';

export const useAppLauncherStore = defineStore('HITL-App-Launcher', {
  state: () => {
    return {
      agent: undefined as IAgent | undefined,
      avatar: DEFAULT_AVATAR_SETTINGS,
      /** We use internal state rather then routes since most of cases are error states */
      state: APP_STATES.DEFAULT,
      error: undefined as AppError | undefined,

      isInitializing: false,
      isLoadingBundle: false,
      isLoadingLauncherSettings: false,

      settings: DEFAULT_APP_SETTINGS as AppLauncherSettings,
      settingsInDB: DEFAULT_APP_SETTINGS as AppLauncherSettings,
      versions: [] as VersionInfo[],
      /** List of currently applied migrations */
      migrations: [] as string[],
      migrationsByRange: {} as MigrationsBySemVerRange,

      isSavingSettings: false,
      isPreparingInstall: false,
      isPreparingUpdate: false,
      isUpdateInProgress: false,
      update: {
        target: '',
        pending: [] as string[],
        complete: [] as string[],
        reports: {} as UpdateReports,
        /** We can retry update on error */
        error: undefined as AppError | undefined,
      },

      component: null as typeof Component | null,
      componentsByKeyVersion: {} as Record<string, typeof Component>,
    };
  },
  getters: {
    /** An error always blocks all other actions */
    appState: state => (state.error ? APP_STATES.APP_ERROR : state.state),
    appError: state => state.error || state.update.error,

    isUserAgent: state => !!state.agent?.ruleGroupIds?.length,
    isUserAdmin: state => {
      const ADMIN_ROLES = [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN];
      return ADMIN_ROLES.includes(state.agent?.role);
    },
    isUserBasicAgent(): boolean {
      return this.isUserAgent && !this.isUserAdmin;
    },
    /** Basic users can't interact with HITL at all */
    canUserInteractWithHITL(): boolean {
      return this.isUserAgent || this.isUserAdmin;
    },

    /**
     * When update is complete target version can be retrieved from "Sequelize Lock" table
     * even if launcher settings are not saved yet.
     */
    updateTargetVersion: state => state.update.target,
    readyToUseVersion(state): string {
      return this.appError
        ? ''
        : this.isUpdateComplete
          ? this.updateTargetVersion || ''
          : state.settingsInDB.appVersion || '';
    },

    isAnyMigrationDone: state => !!state.migrations.length,
    isAppLauncherMigrationDone: state => state.migrations.includes(MIGRATIONS.CONTACT_RULE_GROUPS),
    isAppLauncherSetUp(state): boolean {
      const canInteract = !this.appError && !state.isUpdateInProgress;
      return canInteract && !!this.readyToUseVersion;
    },

    canUseSettingsAPI: state => state.migrations.includes(MIGRATIONS.SETTINGS),
    canUseAgentsAPI: state => state.migrations.includes(MIGRATIONS.AGENTS),
    /** Admins can skip settings screen if they enable it in settings */
    canSkipLauncher(state): boolean {
      return this.isUserAdmin
        && this.isAppLauncherSetUp
        && state.settingsInDB.skipAppLauncher;
    },
    isUpdateComplete: state => {
      return !!state.update.complete.length
        && !state.update.pending.length
        && !state.update.error;
    },

    /**
     * Show buttons only when admin is manually changing app versions
     * rather then using "Install" or "Accept" buttons.
     */
    actionsType(): '' | 'save' | 'update' {
      const routes = [APP_STATES.APP_SETTINGS, APP_STATES.NEEDS_DATA_UPDATE];
      switch (true) {
        case !routes.includes(this.appState):
          return '';
        case this.needsDataUpdate:
          return 'update';
        case this.needsSettingsSave:
          return 'save';
        default:
          return '';
      }
    },

    /** If possible we can suggest "Update To" / "Accept" version to user */
    vSuggest(state): string {
      switch (state.state) {
        case APP_STATES.NO_APP_AVAILABLE:
        case APP_STATES.NO_APP_VERSION_SUPPORT:
          return this.maxVersion;
        case APP_STATES.NO_APP_VERSION:
        case APP_STATES.DATA_UPDATE_ERROR_ADMIN:
          return this.maxVersionByActiveMigration;
        default:
          return '';
      }
    },

    needsSettingsSave: state => !_.isEqual(state.settings, state.settingsInDB),
    needsDataUpdate: state => needsDataUpdate({
      vActive: state.settingsInDB.appVersion,
      vTarget: state.settings.appVersion,
      migrationsByRange: state.migrationsByRange,
    }),

    versionsOrderBySemver: state => orderVersionsBySemVer(state.versions),
    getMigrationsByVersion: state => (version = '') => getMigrationsByAppVersion({
      version,
      migrationsByRange: state.migrationsByRange,
    }),
    /**
     * A rare case when version in settings is lower than required by current migration.
     * Can be caused by manual migration or manual update of "Sequelize" tables.
     */
    isVersionLowerThenMin(): boolean {
      return !!semver.valid(this.readyToUseVersion) && !!semver.valid(this.minAvailableVersion)
        ? semver.lt(this.readyToUseVersion, this.minAvailableVersion)
        : false;
    },

    /** Min available version by current migration or APP_MIN_VERSION */
    minAvailableVersion: state => {
      if (!state.migrations.length) {
        return APP_MIN_VERSION;
      }

      const mLatest = state.migrations.at(-1)!;
      const ranges = _.invert(state.migrationsByRange);
      const suggestion = semver.minVersion(ranges[mLatest] || '')!.version;
      return semver.gte(suggestion, APP_MIN_VERSION) ? suggestion : APP_MIN_VERSION;
    },

    /**
     * We do not allow downgrading to versions
     * that require earlier migrations than the current one.
     */
    availableVersions(): VersionInfo[] {
      return this.versionsOrderBySemver.filter(item => {
        return semver.gte(item.version, this.minAvailableVersion);
      });
    },

    /** Max non-RC version in the versions list */
    maxVersion(): string {
      const maxVersion = this.versionsOrderBySemver.find(item => !item.prerelease.length);
      if (!maxVersion) {
        return '';
      }

      return semver.gte(maxVersion.version, APP_MIN_VERSION)
        ? maxVersion.version
        : '';
    },

    /** Max non-RC version that can be used with the current DB version */
    maxVersionByActiveMigration: state => {
      const mTarget = state.migrations.at(-1);
      if (!mTarget) {
        return '';
      }

      const version = getMaxAppVersionByMigration({
        migration: mTarget,
        versions: state.versions,
        migrationsByRange: state.migrationsByRange,
      });

      return version && semver.gte(version, APP_MIN_VERSION) ? version : '';
    },
  },
});

export const useAppLauncherSetters = () => {
  const store = useAppLauncherStore();

  const setAppSettings = (settings: AppLauncherSettings) => {
    store.settings = _.cloneDeep(settings);
    store.settingsInDB = _.cloneDeep(settings);
  };

  /** Migration names start with number are should be stored in ascending order */
  const setActiveMigrations = (migrations: string[]) => {
    store.migrations = [...migrations].sort();
  };

  /** Change update-related properties at the same time, only provided values are updated */
  const changeUpdateProgress = (options: {
    complete?: string[];
    pending?: string[];
    meta?: object;
    reports?: object;
    error?: AppError | null;
    isInProgress?: boolean;
  } = {}) => {
    const {
      complete = store.update.complete || [],
      pending = store.update.pending || [],
      meta,
      reports = store.update.reports || {},
      isInProgress,
    } = options;

    const target = meta
      ? (meta as UpdateMeta).appVersion || ''
      : store.update.target;

    const error = _.isEmpty(options.error) ? store.update.error : options.error;
    store.isUpdateInProgress = isInProgress ?? (!error && !!pending.length);
    store.update = { target, complete, pending, reports, error } as typeof store.update;
  };

  /** Sync store properties with update result */
  const applyUpdateChanges = () => {
    const settings = { ...store.settingsInDB, appVersion: store.updateTargetVersion };
    setAppSettings(settings);
    setActiveMigrations(store.update.complete);
    clearUpdateProgress();
  };

  /** Reset update status but optionally leave update error for admin to decide */
  const clearUpdateProgress = (error?: AppError) => {
    store.isUpdateInProgress = false;
    store.update = { target: '', complete: [], pending: [], reports: {}, error };
  };

  return {
    setAppSettings,
    setActiveMigrations,
    applyUpdateChanges,
    changeUpdateProgress,
    clearUpdateProgress,
  };
};
